import { useState, useCallback } from 'react'
import { generateMusic } from '../../../services/api/aiVideoGeneration'
import useTaskPoller from './useTaskPoller'

export const useMusic = () => {
  const [previewData, setPreviewData] = useState(null)
  const [finalData, setFinalData] = useState(null)
  const [error, setError] = useState(null)
  const [generationType, setGenerationType] = useState(null) // 'preview' or 'final'
  const [isLocalLoading, setIsLocalLoading] = useState(false)

  const {
    startPolling,
    isPolling: isTaskPolling,
    progress,
    status,
    message,
    stopPolling
  } = useTaskPoller({ pollInterval: 3000 })

  const generatePreview = useCallback(
    async (videoUrl, options = {}) => {
      try {
        setError(null)
        setGenerationType('preview')
        setIsLocalLoading(true)

        const payload = {
          video_s3_url: videoUrl,
          user_prompt: options.userPrompt || '',
          music_volume: options.musicVolume || 0.4,
          preview_duration_ms: options.previewDurationMs || 15000,
          allow_multiple_compositions:
            options.allowMultipleCompositions !== undefined
              ? options.allowMultipleCompositions
              : true
        }

        const response = await generateMusic(payload)
        const taskId = response?.data?.task_id

        if (!taskId) {
          throw new Error(
            'Failed to start music preview generation: No Task ID received'
          )
        }

        setIsLocalLoading(false)
        const result = await startPolling(taskId)
        if (result && result.output) {
          setPreviewData(result.output)
          return result.output
        }
      } catch (err) {
        console.error('Error generating music preview:', err)
        setError(err.message || 'Failed to generate music preview')
        setIsLocalLoading(false)
        throw err
      }
    },
    [startPolling]
  )

  const generateFinal = useCallback(
    async (analysisId, options = {}) => {
      try {
        setError(null)
        setGenerationType('final')
        setIsLocalLoading(true)

        const payload = {
          analysis_id: analysisId,
          music_volume: options.musicVolume || 0.4,
          enable_second_pass:
            options.enableSecondPass !== undefined
              ? options.enableSecondPass
              : true
        }

        const response = await generateMusic(payload)
        const taskId = response?.data?.task_id

        if (!taskId) {
          throw new Error(
            'Failed to start final music generation: No Task ID received'
          )
        }

        setIsLocalLoading(false)
        const result = await startPolling(taskId)
        if (result && result.output) {
          setFinalData(result.output)
          return result.output
        }
      } catch (err) {
        console.error('Error generating final music:', err)
        setError(err.message || 'Failed to generate final music')
        setIsLocalLoading(false)
        throw err
      }
    },
    [startPolling]
  )

  return {
    generatePreview,
    generateFinal,
    previewData,
    finalData,
    isPolling: isTaskPolling || isLocalLoading,
    progress,
    status,
    message,
    error,
    generationType,
    stopPolling
  }
}

export default useMusic
